// AP-167 · Contexto global de notificações do Portal do Cliente.
// Unifica o estado entre o SINO (badge) e a página "Notificações" para que
// qualquer ação de leitura (abrir notificação, abrir processo, marcar todas)
// atualize o badge em tempo real — sem precisar de F5.
//
// Exposto: unread, items, loading, refresh(), markRead(id), markAllRead(),
// markReadByProcess(pid). O polling continua ocorrendo em background.
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { portalApi, usePortalAuth } from "./PortalAuthContext";
import { toast } from "sonner";

const Ctx = createContext({
  unread: 0,
  items: [],
  loading: false,
  refresh: async () => {},
  markRead: async (_id) => {},
  markAllRead: async () => {},
  markReadByProcess: async (_pid) => {},
});

export function PortalNotificationsProvider({ children, pollMs = 15000 }) {
  const { client } = usePortalAuth();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal/notifications/unread-count");
      const n = Number(data?.unread || 0);
      const prev = prevRef.current;
      if (prev != null && n > prev) {
        toast.info("🔔 Nova notificação recebida", { duration: 4000 });
      }
      prevRef.current = n;
      setUnread(n);
      return n;
    } catch { return null; }
  }, []);

  const fetchItems = useCallback(async (filter = "all") => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/notifications", { params: { filter } });
      setItems(data?.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchUnread(), fetchItems("all")]);
  }, [fetchUnread, fetchItems]);

  // Polling + visibilidade — só roda quando há cliente autenticado.
  useEffect(() => {
    if (!client) { setUnread(0); setItems([]); return; }
    let alive = true;
    const tick = async () => { if (alive) await fetchUnread(); };
    tick();
    const iv = setInterval(tick, pollMs);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false; clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [client, pollMs, fetchUnread]);

  // Otimista: baixa o contador localmente e re-sincroniza com o servidor.
  const _optimisticDec = (dec = 1) => setUnread((u) => Math.max(0, u - dec));
  const _optimisticMarkRead = (predicate) => {
    setItems((arr) => arr.map((n) => (predicate(n) && !n.is_read)
      ? { ...n, is_read: true, read_at: new Date().toISOString() }
      : n));
  };

  const markRead = useCallback(async (id) => {
    if (!id) return;
    const target = items.find((x) => x.id === id);
    if (target && target.is_read) return; // já lida
    _optimisticMarkRead((n) => n.id === id);
    _optimisticDec(1);
    try {
      await portalApi.post(`/portal/notifications/${id}/read`);
    } catch { /* silent */ }
    await fetchUnread();
  }, [items, fetchUnread]);

  const markAllRead = useCallback(async () => {
    _optimisticMarkRead(() => true);
    setUnread(0);
    try {
      await portalApi.post("/portal/notifications/mark-all-read");
    } catch { /* silent */ }
    await fetchUnread();
  }, [fetchUnread]);

  const markReadByProcess = useCallback(async (pid) => {
    if (!pid) return;
    // Otimista: marca localmente qualquer notificação com ref_id==pid.
    const affected = items.filter((n) => n.ref_id === pid && !n.is_read).length;
    if (affected > 0) {
      _optimisticMarkRead((n) => n.ref_id === pid);
      _optimisticDec(affected);
    }
    try {
      await portalApi.post(`/portal/notifications/read-by-process/${pid}`);
    } catch { /* silent — endpoint pode não existir em preview antigo */ }
    await fetchUnread();
  }, [items, fetchUnread]);

  const value = useMemo(() => ({
    unread, items, loading, refresh, markRead, markAllRead, markReadByProcess,
    fetchItems,
  }), [unread, items, loading, refresh, markRead, markAllRead, markReadByProcess, fetchItems]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePortalNotifications = () => useContext(Ctx);
